import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Building2, ClipboardList, MessageSquare, Tags, UserPlus, Users } from 'lucide-react'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'
import { useUsers } from '@/hooks/use-admin-users'
import { useOrganisations } from '@/hooks/use-admin-organisations'
import { useRubricCorpora, useSkillDomains } from '@/hooks/use-evaluation-rubrics-admin'
import { useFeedbackItems } from '@/hooks/use-feedback'
import { isProductFeedbackEnabled } from '@/constants'
import { accessRequestEnabled, getAccessRequestConfig, getSignupMode } from '@/constants/signup'
import { getPlatformModules } from '@/lib/platform-modules'
import { DashboardMetricCard, DashboardMetricGrid } from '@/components/dashboard'
import { DashboardSectionAction } from '@/components/dashboard/dashboard-section-action'
import { DashboardSectionTitle } from '@/components/dashboard/dashboard-section-title'
import {
  DashboardSectionSkeleton,
  DashboardStatsSkeleton,
} from '@/components/dashboard/dashboard-section-skeleton'
import { PersonIdentityCell } from '@/components/person-identity-cell'
import { cn } from '@/lib/utils'
import { adminPath, type AdminBasePath } from '@/components/admin/use-admin-base-path'
import { AdminUserGroupBadges, adminUserToPersonModel } from '@/lib/admin-user-display'
import { Button } from '@/components/ui/button'

const LIST_LIMIT = 500
const PREVIEW_LIMIT = 5

function formatListCount(count: number): { value: string; description?: string } {
  if (count >= LIST_LIMIT) {
    return { value: `${LIST_LIMIT}+`, description: 'Showing first page only' }
  }
  return { value: String(count) }
}

function countLabel(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural
}

interface PlatformDashboardSectionsProps {
  basePath?: AdminBasePath
}

function FeedbackMetricCard() {
  const { total } = useFeedbackItems({})
  return (
    <DashboardMetricCard
      title={countLabel(total, 'Feedback item', 'Feedback items')}
      value={total}
      icon={MessageSquare}
    />
  )
}

function QuietPlatformLanding({ basePath }: { basePath: AdminBasePath }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const enquiriesEnabled = accessRequestEnabled() || getSignupMode() === 'approval'
  const enquiriesLabel = getAccessRequestConfig().adminLabel ?? 'Enquiries'

  if (!enquiriesEnabled) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
        <p className="text-sm font-medium">Platform home</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          No platform modules are enabled for this tenant yet.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
      <UserPlus className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
      <p className="text-sm font-medium">{enquiriesLabel}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        Review new messages and follow up when you are ready.
      </p>
      <Button
        type="button"
        variant="outline"
        className="mt-6"
        onClick={() =>
          navigateWithThreadPreservation(navigate, adminPath(basePath, 'waitlist'), searchParams)
        }
      >
        Open {enquiriesLabel}
      </Button>
    </div>
  )
}

function PlatformModuleDashboard({
  basePath,
  hasUsers,
  hasOrganisations,
  hasRubrics,
  hasSkills,
  hasFeedback,
}: {
  basePath: AdminBasePath
  hasUsers: boolean
  hasOrganisations: boolean
  hasRubrics: boolean
  hasSkills: boolean
  hasFeedback: boolean
}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useUsers({ limit: LIST_LIMIT })
  const {
    data: organisations = [],
    isLoading: orgsLoading,
    error: orgsError,
  } = useOrganisations({ limit: LIST_LIMIT })
  const {
    data: rubricCorpora = [],
    isLoading: rubricsLoading,
    error: rubricsError,
  } = useRubricCorpora()
  const {
    data: skillDomains = [],
    isLoading: domainsLoading,
    error: domainsError,
  } = useSkillDomains()

  const userCount = formatListCount(users.length)
  const orgCount = formatListCount(organisations.length)

  const publishedRubrics = rubricCorpora.filter((corpus) => corpus.status === 'published').length
  const rubricDescription =
    rubricCorpora.length > 0
      ? `${publishedRubrics} published, ${rubricCorpora.length - publishedRubrics} other`
      : undefined

  const recentUsers = useMemo(
    () =>
      [...users]
        .sort((a, b) => new Date(b.date_joined).getTime() - new Date(a.date_joined).getTime())
        .slice(0, PREVIEW_LIMIT),
    [users],
  )

  const previewOrganisations = useMemo(
    () =>
      [...organisations]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, PREVIEW_LIMIT),
    [organisations],
  )

  const metricsLoading =
    (hasUsers && usersLoading) ||
    (hasOrganisations && orgsLoading) ||
    (hasRubrics && rubricsLoading) ||
    (hasSkills && domainsLoading)
  const metricsError =
    (hasUsers ? usersError : undefined) ||
    (hasOrganisations ? orgsError : undefined) ||
    (hasRubrics ? rubricsError : undefined) ||
    (hasSkills ? domainsError : undefined)

  return (
    <div className="space-y-6">
      {metricsLoading ? (
        <DashboardStatsSkeleton count={4} maxColumns={4} />
      ) : metricsError ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center text-sm">
          <p className="font-medium text-destructive">Could not load platform overview</p>
          <p className="mt-1 text-muted-foreground">
            {(metricsError as Error).message ?? 'Something went wrong.'}
          </p>
        </div>
      ) : (
        <DashboardMetricGrid maxColumns={4}>
          {hasUsers && (
            <DashboardMetricCard
              title={countLabel(users.length, 'User', 'Users')}
              value={userCount.value}
              description={userCount.description}
              icon={Users}
            />
          )}
          {hasOrganisations && (
            <DashboardMetricCard
              title={countLabel(organisations.length, 'Organisation', 'Organisations')}
              value={orgCount.value}
              description={orgCount.description}
              icon={Building2}
            />
          )}
          {hasRubrics && (
            <DashboardMetricCard
              title={countLabel(rubricCorpora.length, 'Rubric', 'Rubrics')}
              value={rubricCorpora.length}
              description={rubricDescription}
              icon={ClipboardList}
            />
          )}
          {hasSkills && (
            <DashboardMetricCard
              title={countLabel(skillDomains.length, 'Skills domain', 'Skills domains')}
              value={skillDomains.length}
              icon={Tags}
            />
          )}
          {hasFeedback && <FeedbackMetricCard />}
        </DashboardMetricGrid>
      )}

      {!hasUsers ? null : usersLoading ? (
        <DashboardSectionSkeleton cards={PREVIEW_LIMIT} />
      ) : usersError ? null : (
        <section className="space-y-4" aria-labelledby="platform-recent-users-heading">
          <DashboardSectionTitle
            id="platform-recent-users-heading"
            title="Recently joined users"
            action={
              <DashboardSectionAction
                onClick={() =>
                  navigateWithThreadPreservation(
                    navigate,
                    adminPath(basePath, 'users'),
                    searchParams,
                  )
                }
              >
                See all
              </DashboardSectionAction>
            }
          />

          {recentUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
              <Users className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">No users yet</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Invite or create users from the Users page.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentUsers.map((user) => {
                const person = adminUserToPersonModel(user)
                return (
                <button
                  key={user.id}
                  type="button"
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-lg border bg-card p-3 text-left text-sm',
                    'transition-colors hover:bg-muted/50',
                  )}
                  onClick={() =>
                    navigateWithThreadPreservation(
                      navigate,
                      adminPath(basePath, `users/${user.id}`),
                      searchParams,
                    )
                  }
                >
                  <PersonIdentityCell
                    className="min-w-0 flex-1"
                    size="sm"
                    name={person.name}
                    subline={person.subtitle}
                    colorSeed={person.colorSeed}
                  />
                  <AdminUserGroupBadges
                    groups={user.groups}
                    maxVisible={2}
                    whenEmpty="hidden"
                    className="max-w-[40%] shrink-0 justify-end"
                  />
                </button>
              )})}
            </div>
          )}
        </section>
      )}

      {!hasOrganisations ? null : orgsLoading ? (
        <DashboardSectionSkeleton cards={PREVIEW_LIMIT} />
      ) : orgsError ? null : (
        <section className="space-y-4" aria-labelledby="platform-organisations-heading">
          <DashboardSectionTitle
            id="platform-organisations-heading"
            title="Organisations"
            action={
              <DashboardSectionAction
                onClick={() =>
                  navigateWithThreadPreservation(
                    navigate,
                    adminPath(basePath, 'organisations'),
                    searchParams,
                  )
                }
              >
                See all
              </DashboardSectionAction>
            }
          />

          {previewOrganisations.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
              <Building2 className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">No organisations yet</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Create organisations from the Organisations page.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {previewOrganisations.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-lg border bg-card p-3 text-left text-sm',
                    'transition-colors hover:bg-muted/50',
                  )}
                  onClick={() =>
                    navigateWithThreadPreservation(
                      navigate,
                      adminPath(basePath, 'organisations'),
                      searchParams,
                    )
                  }
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{org.name}</div>
                    {org.industry ? (
                      <div className="truncate text-xs text-muted-foreground">{org.industry}</div>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  )
}

export function PlatformDashboardSections({ basePath = '/platform' }: PlatformDashboardSectionsProps) {
  const modules = getPlatformModules()
  const hasUsers = modules.includes('users')
  const hasOrganisations = modules.includes('organisations')
  const hasRubrics = modules.includes('rubrics')
  const hasSkills = modules.includes('skills')
  const hasFeedback = modules.includes('feedback') && isProductFeedbackEnabled()
  const hasAnyModuleCards = hasUsers || hasOrganisations || hasRubrics || hasSkills || hasFeedback

  if (!hasAnyModuleCards) {
    return <QuietPlatformLanding basePath={basePath} />
  }

  return (
    <PlatformModuleDashboard
      basePath={basePath}
      hasUsers={hasUsers}
      hasOrganisations={hasOrganisations}
      hasRubrics={hasRubrics}
      hasSkills={hasSkills}
      hasFeedback={hasFeedback}
    />
  )
}

/** @deprecated Use PlatformDashboardSections */
export const AdminDashboardSections = PlatformDashboardSections
